import { ComponentProps, CSSProperties, useMemo } from 'react';
import { getSpeakerLayoutBorderKey } from '@goldcast/utils';
import { MediaContentResponse } from '@goldcast/api/content';
import { CompositeSegment } from '@/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { layoutConfigMap } from '@/player-config/src/types/speaker-types';
import { ResponsiveContainer } from '@/components/atoms/ResponsiveContainer';
import { AlignedRecordings, ClipMetadata } from '@/player-config/src/types/clip-types';
import { SizeType } from '@/player-config/src/types/asset-types';
import RemotionThumbnail from '@/App/remotion/MainPlayer/RemotionThumbnail/RemotionThumbnail';
import { getBroadcastBackgroundImageUrl } from '@/Pages/Sessions/utils';
import { getContentBroadcastId, isUploadedVideo } from '@/player-config/src/utils/mediaContentUtils';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';
import { selectAlignedRecordings } from '@/player-config/src/selectors/contentMetadataStoreSelectors';

const getThumbnailForVideoId = ({
  videoId,
  recordings,
  mediaContent
}: {
  videoId: string;
  recordings: AlignedRecordings[];
  mediaContent?: MediaContentResponse;
}) => {
  const individualRecordingThumbnail = recordings.find(a => a.id === videoId)?.thumbnail;
  if (individualRecordingThumbnail) {
    return individualRecordingThumbnail;
  }

  if (mediaContent) {
    return getBroadcastBackgroundImageUrl(mediaContent);
  }

  return undefined;
};

function SceneThumbnail({
  playerConfig,
  videoSize,
  thumbnailFrameToDisplay,
  compositeSegment,
  compositionHeight,
  compositionWidth,
  parentWidth = '40px',
  mediaContent,
  croppedImageUrl,
  showBackgroundBlur = false,
  sceneThumbnailResponsiveContainerChildClassnames = ''
}: {
  playerConfig: ClipMetadata['player_config'];
  videoSize: SizeType;
  thumbnailFrameToDisplay: number;
  compositeSegment: CompositeSegment;
  compositionHeight: number;
  compositionWidth: number;
  parentWidth?: ComponentProps<typeof ResponsiveContainer>['parentWidth'];
  mediaContent: MediaContentResponse;
  croppedImageUrl?: string;
  showBackgroundBlur?: boolean;
  sceneThumbnailResponsiveContainerChildClassnames?: ComponentProps<typeof ResponsiveContainer>['childClassnames'];
}) {
  const broadcastId = getContentBroadcastId(mediaContent) ?? '';
  const recordings = useContentMetadataStore(selectAlignedRecordings(broadcastId));

  const isUploadedAsset = useMemo(() => {
    if (!mediaContent) {
      return false;
    }
    return isUploadedVideo(mediaContent.media_source_type);
  }, [mediaContent]);

  const backgroundStyle = useMemo(() => {
    const background = compositeSegment.details.background;
    if (background.type === 'IMAGE_URL') {
      return { backgroundImage: `url(${background.value})`, backgroundSize: 'cover', backgroundPosition: 'center' };
    }

    // For type === 'COLOR' or others
    return { backgroundColor: background.value };
  }, [compositeSegment.details?.background]);

  const layoutDetailsAtFrameToDisplay = compositeSegment.layoutChanges.find(
    change => change.fromFrame <= thumbnailFrameToDisplay && change.toFrame > thumbnailFrameToDisplay
  );

  const { videoThumbnails, areAllThumbnailsSame } = useMemo(() => {
    if (!layoutDetailsAtFrameToDisplay?.videoIds) {
      return { videoThumbnails: [], areAllThumbnailsSame: false };
    }

    let thumbnails: (string | undefined)[];
    if (isUploadedAsset && mediaContent) {
      thumbnails = croppedImageUrl ? layoutDetailsAtFrameToDisplay.videoIds.map(_ => croppedImageUrl) : [];
    } else {
      thumbnails = layoutDetailsAtFrameToDisplay.videoIds.map(video =>
        getThumbnailForVideoId({ videoId: video, recordings, mediaContent })
      ) as string[];
    }

    const allSame = thumbnails.length > 0 && thumbnails.every(v => v === thumbnails[0]);
    return { videoThumbnails: thumbnails, areAllThumbnailsSame: allSame };
  }, [layoutDetailsAtFrameToDisplay, recordings, isUploadedAsset, mediaContent, croppedImageUrl]);

  const [layout, finalConfig] = useMemo(() => {
    if (!layoutDetailsAtFrameToDisplay) {
      return [null, []];
    }

    const { size, layout, fitMode, hideBorders } = layoutDetailsAtFrameToDisplay;

    if (layout === 'AUDIOGRAM') {
      return [layout, []];
    }

    if (videoThumbnails.length === 1) {
      return [layout, layoutConfigMap[size]['GRID'][fitMode][getSpeakerLayoutBorderKey(!hideBorders)][1]];
    }

    return [
      layout,
      layoutConfigMap[size][layout][fitMode][getSpeakerLayoutBorderKey(!hideBorders)][videoThumbnails.length]
    ];
  }, [layoutDetailsAtFrameToDisplay, videoThumbnails]);

  const Thumbnail = useMemo(() => {
    if (!playerConfig) {
      return <div></div>;
    }

    return layout === 'AUDIOGRAM' ? (
      <RemotionThumbnail
        videoSize={videoSize}
        playerConfig={playerConfig}
        durationInFrames={Math.floor(playerConfig.durationInFrames ?? 1)}
        frameToDisplay={thumbnailFrameToDisplay}
      />
    ) : (
      <div
        style={{
          position: 'relative',
          height: '100%',
          width: '100%',
          ...backgroundStyle
        }}
      >
        {videoThumbnails.map((video, videoIndex) => {
          const videoId = layoutDetailsAtFrameToDisplay?.videoIds[videoIndex];
          const videoCropStyle = videoId && layoutDetailsAtFrameToDisplay?.videoCropPositionStyles?.[videoId];

          // Use videoCropPositionStyles if available, otherwise fall back to finalConfig
          const newStyle = videoCropStyle || (videoIndex === -1 ? {} : finalConfig[videoIndex].style);
          const newContainerStyle = videoIndex === -1 ? { zIndex: -1 } : finalConfig[videoIndex].containerStyle;

          const key = areAllThumbnailsSame ? videoIndex : video;

          return (
            <div key={key} style={{ ...newContainerStyle, borderRadius: '1px' } as CSSProperties}>
              <img src={video || ''} alt="video" style={{ ...newStyle } as CSSProperties} />
            </div>
          );
        })}
      </div>
    );
  }, [
    playerConfig,
    layout,
    videoSize,
    thumbnailFrameToDisplay,
    backgroundStyle,
    videoThumbnails,
    layoutDetailsAtFrameToDisplay?.videoIds,
    layoutDetailsAtFrameToDisplay?.videoCropPositionStyles,
    finalConfig,
    areAllThumbnailsSame
  ]);

  if (!playerConfig || !layout) {
    return null;
  }

  return (
    <>
      {showBackgroundBlur && (
        <div className="absolute inset-0 overflow-hidden opacity-80">
          <div className="inset-0 z-0 size-full rounded-[3px] bg-cover bg-center opacity-60 blur-[20px]">
            {Thumbnail}
          </div>
        </div>
      )}
      <ResponsiveContainer
        id="scene-thumbnail-container"
        compositionHeight={compositionHeight}
        compositionWidth={compositionWidth}
        parentHeight={'100%'}
        parentWidth={parentWidth}
        childClassnames={sceneThumbnailResponsiveContainerChildClassnames}
      >
        {Thumbnail}
      </ResponsiveContainer>
    </>
  );
}

export default SceneThumbnail;
